import GlobalTextInputField from "@components/inputs/GlobalTextInputField";
import DataPill from "@components/pills/DataPill";
import {
  ReactElement,
  forwardRef,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { clsxm } from "../../../utils/clxm/clxm";
import { useClickOutside } from "../../../utils/hooks/useClickOutSide";
import { useForkRef } from "../../../utils/hooks/useForkRef";
import { PSelectOption, SelectOption } from "./SelectOption";
import { ArrowDownIcon } from "./icons/ArrowDownIcon";

export const INPUT_HEIGHT_CLS = "h-[48px] 2xl:h-[56px] 4xl:h-[80px]";
export const INPUT_FONT_CLS =
  "Body-M-Medium 2xl:Body-L-Medium 4xl:Body-4XL-Medium";

export type PSelect = {
  options: PSelectOption["option"][];
  value?: string | number;
  onChange: (value: string | number) => void;
  getSelectCls?: (expanded: boolean) => string;
  getOptionCls?: (isActive: boolean) => string;
  getOptionWrapperCls?: () => string;
  getSelectedOptionCls?: () => string;
  getSelectedValueCls?: string;
  label?: string;
  color?: string;
  baseColor?: string;
  borderColor?: string;
  expandedBorderColor?: string;
  titleColor?: string;
  optionsHeader?: ReactElement;
  displaySelectedValue?: string | ReactElement;
  enableSearch?: boolean;
  searchableLabels?: any;
  onExpandedChange?: (expanded: boolean) => void;
  expanded?: boolean;
  dropdownMaxHeight?: string;
  required?: boolean;
  disabled?: boolean;
  fontWeight?: string;
} & Pick<PSelectOption, "getOptionCls">;

export const getRotateButtonCls = (expanded: boolean) =>
  clsxm(
    "text-text-base font-normal s-[20px] 2xl:s-[22px] 4xl:s-[26px] origin-center transition-all duration-400",
    expanded ? "rotate-180" : "rotate-0",
  );

export const Select = forwardRef<
  HTMLInputElement,
  PSelect & { expanded?: boolean }
>(function SelectInput(
  {
    options,
    onChange,
    value,
    getSelectCls,
    getOptionCls,
    getOptionWrapperCls,
    getSelectedOptionCls,
    label,
    optionsHeader,
    displaySelectedValue,
    color,
    baseColor,
    borderColor,
    expandedBorderColor,
    titleColor = "text-text-base",
    enableSearch,
    searchableLabels,
    onExpandedChange,
    expanded: controlledExpanded, // New prop for controlled expansion
    dropdownMaxHeight,
    required = false,
    disabled = false,
    fontWeight = "font-normal",
  },
  ref,
) {
  const [expanded, setExpanded] = useState(false);

  useEffect(() => {
    // Sync the controlledExpanded prop with the internal expanded state
    if (controlledExpanded !== undefined) {
      setExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  const [searchQuery, setSearchQuery] = useState(""); // State for search query

  const handleSelect = (value: string | number) => {
    setExpanded(false);
    onChange && onChange(value);
  };

  const divRef = useClickOutside<HTMLDivElement>({
    handler: () => setExpanded(false),
  });
  const rootRef = useForkRef<HTMLDivElement>(divRef, ref);

  const displayValue = useMemo(() => {
    if (value === undefined || value === null) {
      return "Select"; // Default text when no option is selected
    }
    const selectedItem = options.find((item) => item.value === value);
    return selectedItem?.selectedLabel || selectedItem?.label || "Select";
  }, [value, options]);

  const fieldRef = useRef<HTMLElement | null>(null);

  const filteredOptions = useMemo(() => {
    if (searchQuery.trim() === "" || !searchableLabels) return options;
    return options.filter((option, index) => {
      const labelsToSearch = searchableLabels[index]; // Get array of searchable fields
      return labelsToSearch.some((label: string) =>
        label.toLowerCase().includes(searchQuery.toLowerCase()),
      );
    });
  }, [searchQuery, options, searchableLabels]);

  useEffect(() => {
    if (controlledExpanded !== undefined) {
      setExpanded(controlledExpanded);
    }
  }, [controlledExpanded]);

  const handleExpandToggle = (newExpanded: boolean) => {
    setExpanded(newExpanded);
    onExpandedChange?.(newExpanded);
  };

  return (
    <div className="flex flex-col w-full relative">
      {label && (
        <>
          <div className="flex items-center gap-2">
            <span className={clsxm("font-semibold text-[10px]", titleColor)}>
              {label}
            </span>
            {required && (
              <DataPill label="REQ" tone={value ? "green" : "red"} />
            )}
          </div>
          <div className="my-[3px]" />
        </>
      )}
      <div
        ref={rootRef}
        role="button"
        aria-disabled={disabled}
        className={`relative flex flex-col w-full ${disabled ? "cursor-not-allowed opacity-70" : ""}`}
        onClick={() => {
          if (!disabled) handleExpandToggle(!expanded);
        }}
      >
        <div
          className={clsxm(
            "flex flex-col justify-between items-center",
            expanded
              ? `border-t border-l border-r border-b-0 border-${expandedBorderColor} rounded-[8px_8px_0px_0px]`
              : `border-[1px] border-${borderColor} rounded-[8px]`,
            "overflow-hidden",
            expanded && "shadow-lg",
          )}
        >
          <div
            className={clsxm(
              "flex flex-row justify-between items-center",
              INPUT_HEIGHT_CLS,
              INPUT_FONT_CLS,
              `4xl:Body-2XL-Medium px-[10px] w-full text-text-base bg-${
                expanded ? color : baseColor
              } gap-3`,
              getSelectCls && getSelectCls(expanded),
            )}
            ref={(node) => {
              if (node) {
                fieldRef.current = node;
              }
            }}
          >
            <div
              className={clsxm(
                `w-full text-text-base ${fontWeight} text-left text-[12px]`,
                getSelectedOptionCls && getSelectedOptionCls(),
              )}
            >
              {displaySelectedValue || displayValue}
            </div>
            <ArrowDownIcon className={getRotateButtonCls(expanded)} />
          </div>

          {expanded && (
            <div
              className={clsxm(
                `absolute border-t-0 border-l border-r border-b border-${expandedBorderColor} left-0 rounded-[0px_0px_8px_8px] flex flex-col w-full z-[9999] shadow-lg`,
                getOptionWrapperCls && getOptionWrapperCls(),
              )}
              style={{
                top: fieldRef.current?.clientHeight ?? "100%",
                borderTop: "1px solid var(--border-subtle)",
                backgroundColor: "var(--bg-page-body)",
                maxHeight: dropdownMaxHeight || "40vh",
                overflowY: "auto",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {optionsHeader}
              {enableSearch && (
                <div className="p-[10px]">
                  <GlobalTextInputField
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search..."
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              )}
              {filteredOptions.map((item, index) => (
                <div key={item.value}>
                  <SelectOption
                    onSelect={handleSelect}
                    styles={{
                      height: fieldRef.current?.clientHeight
                        ? fieldRef.current?.clientHeight
                        : "44px",
                    }}
                    isActive={item.value === value}
                    getOptionCls={getOptionCls}
                    option={item}
                    onExpandedChange={onExpandedChange}
                  />
                  {index < filteredOptions.length - 1 && (
                    <div className="border-t border-border-subtle" />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
});
